import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { HubShell, Section, InfoGrid, hubHead } from "@/components/work-hubs/HubShell";

export const Route = createFileRoute("/remote-jobs/")({
  head: () =>
    hubHead(
      "/remote-jobs",
      "Non-Phone Remote Jobs Guide — Chat, Data & Moderation Roles | DrHint",
      "Find remote work with no phone calls: chat support, content moderation, AI data rating and transcription. Check your eligibility in one minute.",
    ),
  component: RemoteJobsPage,
});

const ROLES = [
  { title: "Chat & email support", body: "Answer customer questions in writing through live chat or ticket tools like Zendesk. Needs clear written English and roughly 40+ WPM typing. Usually shift-based." },
  { title: "Content moderation", body: "Review posts, images or ads against a platform's rules. Work is mostly asynchronous but can involve disturbing content — read the role description carefully." },
  { title: "AI data rating & training", body: "Rate search results or chatbot answers, or write example responses. Often project-based with flexible hours. Subject experts (maths, coding, languages) get higher rates." },
  { title: "Transcription & localisation", body: "Turn audio into text or check translations. Pay is per audio minute or per word, so speed and accuracy matter directly." },
];

function RemoteJobsPage() {
  const [wpm, setWpm] = useState(40);
  const [laptop, setLaptop] = useState(true);
  const [net, setNet] = useState(true);
  const [english, setEnglish] = useState(true);
  const [quiet, setQuiet] = useState(false);

  const matches: string[] = [];
  if (laptop && net) {
    if (english && wpm >= 40) matches.push("Chat & email support");
    if (english) matches.push("Content moderation", "AI data rating & training");
    if (wpm >= 50) matches.push("Transcription & localisation");
  }
  const missing: string[] = [];
  if (!laptop) missing.push("a laptop or desktop computer (most roles do not allow phone-only work)");
  if (!net) missing.push("a stable broadband connection");
  if (!english) missing.push("comfortable written English (or a target language for localisation)");
  if (wpm < 40) missing.push("typing speed of about 40 WPM — free typing tests help you practise");

  return (
    <HubShell
      crumbs={[{ label: "Home", to: "/" }, { label: "Remote Jobs" }]}
      step={1}
      eyebrow="Remote Hub"
      title="Non-Phone Remote Jobs: Which Roles Fit You?"
      intro="Many people want online work but dislike phone calls or cold calling. Several real job categories are done entirely in writing. Use the quick checker below to see which ones match your setup, then read what each role actually involves."
      next={{ to: "/remote-jobs/requirements", label: "Review Equipment & Verification Checklist" }}
    >
      <Section title="Quick eligibility checker">
        <div className="rounded-2xl border border-border bg-card p-5 space-y-5">
          <label className="block">
            <span className="text-sm font-semibold text-foreground">Your typing speed: {wpm} WPM</span>
            <input type="range" min={10} max={100} step={5} value={wpm} onChange={(e) => setWpm(Number(e.target.value))} className="mt-2 w-full accent-[var(--primary)]" />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              ["I have a laptop/desktop", laptop, setLaptop],
              ["I have stable broadband", net, setNet],
              ["I write English comfortably", english, setEnglish],
              ["I have a quiet workspace", quiet, setQuiet],
            ].map(([label, val, set]) => (
              <label key={label as string} className="flex items-center gap-2 rounded-lg border border-border p-3 text-sm text-foreground">
                <input type="checkbox" checked={val as boolean} onChange={(e) => (set as (v: boolean) => void)(e.target.checked)} />
                {label as string}
              </label>
            ))}
          </div>
          <div className="rounded-xl bg-muted/50 p-4 text-sm">
            {matches.length > 0 ? (
              <p className="text-foreground"><b>Good matches:</b> {matches.join(", ")}.</p>
            ) : (
              <p className="text-foreground"><b>No strong match yet.</b></p>
            )}
            {missing.length > 0 && <p className="mt-2 text-muted-foreground">To widen your options, work on: {missing.join("; ")}.</p>}
          </div>
        </div>
      </Section>

      <Section title="Non-phone roles explained">
        <InfoGrid items={ROLES} />
      </Section>

      <Section title="Before you apply anywhere">
        <p>Genuine employers pay you — you never pay them. Be careful of "registration fees", "training kits" or WhatsApp recruiters offering high pay for simple tasks. Apply only through a company's own careers page, which you will find linked in step 3.</p>
        <p>Expect a screening test, an identity check and sometimes a waiting list. Acceptance can take a few days to several weeks, and some projects only hire in certain countries.</p>
      </Section>
    </HubShell>
  );
}
